import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ComposedChart, Area, LineChart
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, ShieldAlert, ShieldCheck,
  Banknote, CreditCard, ArrowUpRight, ArrowDownRight, AlertOctagon,
  Calendar, ChevronUp, ChevronDown, Minus
} from 'lucide-react';
import { cn } from '../../lib/utils';

const fmt = (n) => {
  if (n == null) return '$0';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtShort = (n) => {
  if (n == null) return '$0';
  if (n >= 1000) return '$' + (n / 1000).toFixed(1) + 'k';
  return '$' + Number(n).toFixed(0);
};

// Group transactions by week
function groupByWeek(transactions) {
  const weeks = {};
  for (const tx of transactions || []) {
    const dateStr = tx.date || '';
    // Parse M/DD or MM/DD/YYYY
    const parts = dateStr.split('/');
    if (parts.length < 2) continue;
    const day = parseInt(parts[1]) || 1;
    const weekNum = Math.ceil(day / 7);
    const weekLabel = `Sem ${weekNum}`;
    if (!weeks[weekLabel]) weeks[weekLabel] = { deposits: 0, withdrawals: 0, week: weekLabel, order: weekNum };
    if (tx._type === 'deposit') weeks[weekLabel].deposits += tx.amount || 0;
    else weeks[weekLabel].withdrawals += tx.amount || 0;
  }
  return Object.values(weeks).sort((a, b) => a.order - b.order);
}

// Classify transactions into categories
function classifyTransactions(month) {
  const categories = {
    income: { label: 'Ingresos Verificados', items: [], total: 0, icon: TrendingUp, color: 'text-emerald-600' },
    transfers_in: { label: 'Transferencias Entrantes', items: [], total: 0, icon: ArrowUpRight, color: 'text-blue-600' },
    transfers_out: { label: 'Transferencias Salientes', items: [], total: 0, icon: ArrowDownRight, color: 'text-orange-600' },
    payments: { label: 'Pagos y Compras', items: [], total: 0, icon: CreditCard, color: 'text-red-600' },
    other_withdrawals: { label: 'Otros Retiros', items: [], total: 0, icon: TrendingDown, color: 'text-gray-600' },
  };

  for (const d of month?.real_income_deposits || []) {
    const desc = (d.description || '').toUpperCase();
    if (desc.includes('TRANSFER FROM') || desc.includes('ONLINE TRANSFER FROM')) {
      categories.transfers_in.items.push(d);
      categories.transfers_in.total += d.amount || 0;
    } else {
      categories.income.items.push(d);
      categories.income.total += d.amount || 0;
    }
  }

  for (const d of month?.other_deposits || []) {
    categories.transfers_in.items.push(d);
    categories.transfers_in.total += d.amount || 0;
  }

  for (const w of month?.withdrawals || []) {
    const desc = (w.description || '').toUpperCase();
    if (desc.includes('TRANSFER TO') || desc.includes('ZELLE TO') || desc.includes('ZELLE SENT')) {
      categories.transfers_out.items.push(w);
      categories.transfers_out.total += w.amount || 0;
    } else if (desc.includes('PURCHASE') || desc.includes('POS') || desc.includes('DEBIT CARD') || desc.includes('VISA') || desc.includes('MASTERCARD')) {
      categories.payments.items.push(w);
      categories.payments.total += w.amount || 0;
    } else {
      categories.other_withdrawals.items.push(w);
      categories.other_withdrawals.total += w.amount || 0;
    }
  }

  return categories;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-zinc-900 border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

const BankAnalysisVisuals = ({ analysis }) => {
  const months = analysis?.months || [];
  const isMultiMonth = months.length > 1;

  const { weeklyData, allTransactions, categories, riskScore, risks, monthlyComparison } = useMemo(() => {
    const allTx = [];
    const allCats = {};
    let totalRisk = 0;
    const riskItems = [];

    // Monthly comparison data
    const monthlyData = months.map((m, idx) => {
      const wTotal = (m.withdrawals || []).reduce((s, w) => s + (w.amount || 0), 0);
      const deposits = m.total_deposits || 0;
      const income = m.real_income || deposits;
      const balance = m.ending_balance || 0;
      const label = m.month || m.account_holder || `Mes ${idx + 1}`;

      return {
        label,
        shortLabel: label.length > 10 ? label.slice(0, 10) : label,
        deposits,
        income,
        withdrawals: wTotal,
        netFlow: deposits - wTotal,
        balance,
        nsf: m.nsf_count || 0,
        mca: m.mca_detected || false,
        bank: m.bank_name || 'N/A',
        depositCount: (m.real_income_deposits?.length || 0) + (m.other_deposits?.length || 0),
        withdrawalCount: m.withdrawals_count || (m.withdrawals?.length || 0),
      };
    });

    // Calculate month-over-month changes
    for (let i = 1; i < monthlyData.length; i++) {
      const prev = monthlyData[i - 1];
      const curr = monthlyData[i];
      curr.depositChange = prev.deposits > 0 ? ((curr.deposits - prev.deposits) / prev.deposits * 100) : 0;
      curr.balanceChange = prev.balance > 0 ? ((curr.balance - prev.balance) / prev.balance * 100) : 0;
      curr.incomeChange = prev.income > 0 ? ((curr.income - prev.income) / prev.income * 100) : 0;
    }

    for (const m of months) {
      // Build tx list with _type marker
      for (const d of [...(m.real_income_deposits || []), ...(m.other_deposits || [])]) {
        allTx.push({ ...d, _type: 'deposit' });
      }
      for (const w of (m.withdrawals || [])) {
        allTx.push({ ...w, _type: 'withdrawal' });
      }

      // Classify
      const cats = classifyTransactions(m);
      for (const [key, cat] of Object.entries(cats)) {
        if (!allCats[key]) allCats[key] = { ...cat, items: [], total: 0 };
        allCats[key].items.push(...cat.items);
        allCats[key].total += cat.total;
      }

      // Risk evaluation
      if (m.mca_detected) { totalRisk += 40; riskItems.push({ level: 'critical', label: 'MCA/Prestamista Diario Detectado', desc: 'Se encontraron pagos regulares a prestamistas de alto riesgo' }); }
      if (m.nsf_count > 0) { totalRisk += Math.min(m.nsf_count * 10, 30); riskItems.push({ level: m.nsf_count >= 3 ? 'critical' : 'warning', label: `${m.nsf_count} NSF/Fondos Insuficientes`, desc: 'Rechazos por falta de fondos en la cuenta' }); }
      if (m.negative_balance_days > 0) { totalRisk += Math.min(m.negative_balance_days * 5, 20); riskItems.push({ level: 'warning', label: `${m.negative_balance_days} Dias con Balance Negativo`, desc: 'La cuenta tuvo balance negativo durante estos dias' }); }
    }

    // Low balance risk
    if (analysis?.min_ending_balance < 100 && analysis?.min_ending_balance !== undefined) {
      totalRisk += 15;
      riskItems.push({ level: 'warning', label: 'Balance Final Muy Bajo', desc: `Balance minimo: ${fmt(analysis.min_ending_balance)}` });
    }

    if (riskItems.length === 0) {
      riskItems.push({ level: 'ok', label: 'Sin Riesgos Detectados', desc: 'No se encontraron indicadores de riesgo en los estados de cuenta' });
    }

    return {
      weeklyData: groupByWeek(allTx),
      allTransactions: allTx,
      categories: allCats,
      riskScore: Math.min(totalRisk, 100),
      risks: riskItems,
      monthlyComparison: monthlyData,
    };
  }, [months, analysis]);

  if (!months.length) return null;

  const totalDeposits = months.reduce((s, m) => s + (m.total_deposits || 0), 0);
  const totalWithdrawals = months.reduce((s, m) => s + (m.withdrawals || []).reduce((ws, w) => ws + (w.amount || 0), 0), 0);
  const realIncome = months.reduce((s, m) => s + (m.real_income || 0), 0);
  const nsf = months.reduce((s, m) => s + (m.nsf_count || 0), 0);
  const endingBalance = months[months.length - 1]?.ending_balance ?? 0;
  const excludedDeposits = months.reduce((s, m) => s + ((m.other_deposits || []).reduce((os, o) => os + (o.amount || 0), 0)), 0);

  return (
    <div className="space-y-5" data-testid="bank-analysis-visuals">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary-cards">
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/80 to-white dark:from-emerald-950/30 dark:to-zinc-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ingreso Real</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 mt-1" data-testid="real-income-value">{fmt(realIncome)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{months.reduce((s, m) => s + (m.real_income_deposits?.length || 0), 0)} depositos verificados</p>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
                <Banknote className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 bg-gradient-to-br from-amber-50/80 to-white dark:from-amber-950/30 dark:to-zinc-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Depositos Excluidos</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-400 mt-1" data-testid="excluded-deposits-value">{fmt(excludedDeposits)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Transferencias / Prestamos</p>
              </div>
              <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/40">
                <ArrowUpRight className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50/80 to-white dark:from-blue-950/30 dark:to-zinc-900">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Balance Final</p>
                <p className={cn("text-2xl font-bold mt-1", endingBalance < 100 ? "text-red-600" : "text-blue-700 dark:text-blue-400")} data-testid="ending-balance-value">{fmt(endingBalance)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Ultimo mes analizado</p>
              </div>
              <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/40">
                <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn("border-l-4 bg-gradient-to-br", nsf > 0 ? "border-l-red-500 from-red-50/80 to-white dark:from-red-950/30 dark:to-zinc-900" : "border-l-emerald-500 from-emerald-50/40 to-white dark:from-emerald-950/20 dark:to-zinc-900")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">NSF / Rechazos</p>
                <p className={cn("text-2xl font-bold mt-1", nsf > 0 ? "text-red-600" : "text-emerald-600")} data-testid="nsf-value">{nsf}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{nsf > 0 ? 'Fondos insuficientes' : 'Sin rechazos'}</p>
              </div>
              <div className={cn("p-2.5 rounded-xl", nsf > 0 ? "bg-red-100 dark:bg-red-900/40" : "bg-emerald-100 dark:bg-emerald-900/40")}>
                {nsf > 0 ? <AlertOctagon className="w-5 h-5 text-red-600" /> : <ShieldCheck className="w-5 h-5 text-emerald-600" />}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Multi-Month Comparison Section */}
      {isMultiMonth && monthlyComparison.length > 1 && (
        <>
          {/* Monthly Trend Charts */}
          <div className="grid lg:grid-cols-2 gap-5" data-testid="monthly-comparison">
            {/* Deposits vs Withdrawals by Month */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  Depositos vs Retiros por Mes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={monthlyComparison} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="shortLabel" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={55} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="deposits" name="Depositos" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={45} />
                    <Bar dataKey="withdrawals" name="Retiros" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={45} />
                    <Line type="monotone" dataKey="netFlow" name="Flujo Neto" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366f1' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Balance Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  Tendencia de Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={monthlyComparison} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <defs>
                      <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="shortLabel" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={55} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Area type="monotone" dataKey="balance" name="Balance" fill="url(#balanceGrad)" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4, fill: '#3b82f6' }} />
                    <Line type="monotone" dataKey="income" name="Ingreso Real" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#10b981' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Comparison Table */}
          <Card data-testid="monthly-comparison-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Comparacion Mensual</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">Mes</TableHead>
                      <TableHead className="text-right">Depositos</TableHead>
                      <TableHead className="text-right">Ingreso Real</TableHead>
                      <TableHead className="text-right">Retiros</TableHead>
                      <TableHead className="text-right">Flujo Neto</TableHead>
                      <TableHead className="text-right">Balance Final</TableHead>
                      <TableHead className="text-center">NSF</TableHead>
                      <TableHead className="text-center">Tendencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyComparison.map((m, i) => (
                      <TableRow key={i} data-testid={`month-row-${i}`}>
                        <TableCell className="font-medium">
                          <div>
                            <span className="text-sm">{m.label}</span>
                            {m.bank && m.bank !== 'N/A' && (
                              <p className="text-[10px] text-muted-foreground">{m.bank}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-emerald-600">{fmt(m.deposits)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmt(m.income)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-amber-600">{fmt(m.withdrawals)}</TableCell>
                        <TableCell className={cn("text-right font-mono text-sm font-semibold", m.netFlow >= 0 ? "text-emerald-600" : "text-red-600")}>
                          {m.netFlow >= 0 ? '+' : ''}{fmt(m.netFlow)}
                        </TableCell>
                        <TableCell className={cn("text-right font-mono text-sm", m.balance < 100 ? "text-red-600 font-semibold" : "")}>
                          {fmt(m.balance)}
                        </TableCell>
                        <TableCell className="text-center">
                          {m.nsf > 0 ? (
                            <Badge variant="destructive" className="text-xs">{m.nsf}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {i === 0 ? (
                            <Minus className="w-4 h-4 mx-auto text-muted-foreground" />
                          ) : (
                            <div className="flex items-center justify-center gap-1">
                              {m.depositChange > 5 ? (
                                <ChevronUp className="w-4 h-4 text-emerald-500" />
                              ) : m.depositChange < -5 ? (
                                <ChevronDown className="w-4 h-4 text-red-500" />
                              ) : (
                                <Minus className="w-4 h-4 text-muted-foreground" />
                              )}
                              <span className={cn("text-xs font-medium",
                                m.depositChange > 5 ? "text-emerald-600" :
                                m.depositChange < -5 ? "text-red-600" : "text-muted-foreground"
                              )}>
                                {m.depositChange > 0 ? '+' : ''}{m.depositChange?.toFixed(1) || '0'}%
                              </span>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Averages row */}
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell className="text-sm">Promedio</TableCell>
                      <TableCell className="text-right font-mono text-sm text-emerald-700">
                        {fmt(monthlyComparison.reduce((s, m) => s + m.deposits, 0) / monthlyComparison.length)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmt(monthlyComparison.reduce((s, m) => s + m.income, 0) / monthlyComparison.length)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-amber-700">
                        {fmt(monthlyComparison.reduce((s, m) => s + m.withdrawals, 0) / monthlyComparison.length)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmt(monthlyComparison.reduce((s, m) => s + m.netFlow, 0) / monthlyComparison.length)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmt(monthlyComparison.reduce((s, m) => s + m.balance, 0) / monthlyComparison.length)}
                      </TableCell>
                      <TableCell className="text-center text-sm">{(monthlyComparison.reduce((s, m) => s + m.nsf, 0) / monthlyComparison.length).toFixed(1)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Weekly Chart + Risk Panel side by side */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Weekly Chart - 2/3 */}
        <Card className="lg:col-span-2" data-testid="weekly-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Depositos vs Retiros por Semana</CardTitle>
          </CardHeader>
          <CardContent>
            {weeklyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={weeklyData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={55} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="deposits" name="Depositos" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="withdrawals" name="Retiros" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Line type="monotone" dataKey="deposits" name="Tendencia Dep." stroke="#059669" strokeWidth={2} dot={false} strokeDasharray="5 5" legendType="none" />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No hay datos semanales disponibles</p>
            )}
          </CardContent>
        </Card>

        {/* Risk Indicators - 1/3 */}
        <Card data-testid="risk-indicators">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" />
              Indicadores de Riesgo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Risk score */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-muted-foreground">Nivel de Riesgo</span>
                <span className={cn("text-sm font-bold",
                  riskScore >= 60 ? "text-red-600" : riskScore >= 30 ? "text-amber-600" : "text-emerald-600"
                )} data-testid="risk-score">
                  {riskScore >= 60 ? 'ALTO' : riskScore >= 30 ? 'MEDIO' : 'BAJO'}
                </span>
              </div>
              <Progress
                value={riskScore}
                className={cn("h-2.5 rounded-full",
                  riskScore >= 60 ? "[&>div]:bg-red-500" : riskScore >= 30 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500"
                )}
              />
            </div>

            <Separator />

            {/* Individual risks */}
            <div className="space-y-2.5">
              {risks.map((r, i) => (
                <div key={i} className={cn("flex items-start gap-2.5 p-2.5 rounded-lg text-sm",
                  r.level === 'critical' ? "bg-red-50 dark:bg-red-950/30" :
                  r.level === 'warning' ? "bg-amber-50 dark:bg-amber-950/30" :
                  "bg-emerald-50 dark:bg-emerald-950/30"
                )}>
                  <div className={cn("w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                    r.level === 'critical' ? "bg-red-500" :
                    r.level === 'warning' ? "bg-amber-500" :
                    "bg-emerald-500"
                  )} />
                  <div>
                    <p className="font-medium leading-tight">{r.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Deposit vs Withdrawal ratio */}
            <Separator />
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Flujo de Efectivo</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 text-right">
                  <p className="text-xs text-emerald-600 font-medium">{fmt(totalDeposits)}</p>
                  <p className="text-[10px] text-muted-foreground">Entradas</p>
                </div>
                <div className="w-24 h-3 rounded-full bg-gray-200 dark:bg-zinc-800 overflow-hidden flex">
                  <div className="bg-emerald-500 h-full transition-all" style={{ width: `${totalDeposits + totalWithdrawals > 0 ? (totalDeposits / (totalDeposits + totalWithdrawals) * 100) : 50}%` }} />
                  <div className="bg-amber-500 h-full transition-all" style={{ width: `${totalDeposits + totalWithdrawals > 0 ? (totalWithdrawals / (totalDeposits + totalWithdrawals) * 100) : 50}%` }} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-amber-600 font-medium">{fmt(totalWithdrawals)}</p>
                  <p className="text-[10px] text-muted-foreground">Salidas</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Classification Table */}
      <Card data-testid="transactions-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Clasificacion de Transacciones</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Categoria</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">% del Flujo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(categories).map(([key, cat]) => {
                if (cat.items.length === 0) return null;
                const Icon = cat.icon;
                const grandTotal = totalDeposits + totalWithdrawals;
                const pct = grandTotal > 0 ? (cat.total / grandTotal * 100).toFixed(1) : 0;
                return (
                  <TableRow key={key} data-testid={`category-row-${key}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Icon className={cn("w-4 h-4", cat.color)} />
                        <span>{cat.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className="font-mono">{cat.items.length}</Badge>
                    </TableCell>
                    <TableCell className={cn("text-right font-semibold font-mono", cat.color)}>
                      {fmt(cat.total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-zinc-800 overflow-hidden">
                          <div className={cn("h-full rounded-full",
                            key === 'income' ? "bg-emerald-500" :
                            key === 'transfers_in' ? "bg-blue-500" :
                            key === 'transfers_out' ? "bg-orange-500" :
                            key === 'payments' ? "bg-red-500" : "bg-gray-500"
                          )} style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Top transactions detail */}
          <Separator className="my-4" />
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Transacciones Principales</p>
            <div className="grid gap-1.5 max-h-[300px] overflow-y-auto pr-1">
              {allTransactions
                .sort((a, b) => (b.amount || 0) - (a.amount || 0))
                .slice(0, 15)
                .map((tx, i) => (
                  <div key={i} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/50 text-sm" data-testid={`top-tx-${i}`}>
                    <div className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0",
                      tx._type === 'deposit' ? "bg-emerald-500" : "bg-amber-500"
                    )} />
                    <span className="text-xs text-muted-foreground w-14 flex-shrink-0">{tx.date || '-'}</span>
                    <span className="truncate flex-1 text-xs">{tx.description || 'Sin descripcion'}</span>
                    <span className={cn("font-mono text-xs font-semibold flex-shrink-0",
                      tx._type === 'deposit' ? "text-emerald-600" : "text-amber-600"
                    )}>
                      {tx._type === 'deposit' ? '+' : '-'}{fmt(tx.amount)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankAnalysisVisuals;
